import { Brain, AlertTriangle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ATLAS Load Analysis — broker-facing bid helper.
 *
 * Renders the analysis cached on `direct_loads.atlas_analysis`, generated ONCE
 * when the shipper posted the load. This component NEVER calls AI — it only
 * reads the stored value. It is factual haul intelligence: no rate, no bid
 * amount, no pricing guidance of any kind.
 */

export interface AtlasLoadAnalysis {
  version?: number;
  summary?: string;
  transit?: string;
  driver?: string;
  stops?: string;
  timing?: string;
  timing_flag?: "ok" | "tight" | "infeasible" | "unknown" | string;
  equipment?: string;
  considerations?: string[];
  generated_at?: string;
}

export function parseAtlasAnalysis(raw: unknown): AtlasLoadAnalysis | null {
  if (!raw) return null;
  let obj: any = raw;
  if (typeof raw === "string") {
    try { obj = JSON.parse(raw); } catch { return null; }
  }
  if (!obj || typeof obj !== "object") return null;
  const summary = typeof obj.summary === "string" ? obj.summary.trim() : "";
  if (!summary) return null;
  return obj as AtlasLoadAnalysis;
}

const TIMING_STYLES: Record<string, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  ok: { label: "Timing feasible", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  tight: { label: "Tight timing", cls: "border-amber-200 bg-amber-50 text-amber-800", Icon: AlertTriangle },
  infeasible: { label: "Timing at risk", cls: "border-destructive/30 bg-destructive/5 text-destructive", Icon: AlertTriangle },
};

interface Props {
  analysis: AtlasLoadAnalysis;
  className?: string;
}

export default function AtlasLoadAnalysisCard({ analysis, className }: Props) {
  const timing = analysis.timing_flag ? TIMING_STYLES[analysis.timing_flag] : undefined;

  return (
    <div className={cn("overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm", className)}>
      {/* Header — Atlas branding */}
      <div className="flex items-center gap-2 bg-gradient-to-r from-brand-teal to-[#026E68] px-3 py-2">
        <Brain className="h-4 w-4 text-white" />
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-white">Atlas load analysis</h4>
        <span className="ml-auto rounded-full border border-white/40 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
          Factual · no pricing
        </span>
      </div>

      <div className="space-y-2 p-3">
        {analysis.summary && (
          <p className="text-[12px] font-medium leading-relaxed text-foreground">{analysis.summary}</p>
        )}

        {timing && (
          <div className={cn("inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1", timing.cls)}>
            <timing.Icon className="h-3.5 w-3.5 shrink-0" />
            <span className="text-[11px] font-semibold">{timing.label}</span>
          </div>
        )}

        <p className="border-t border-border pt-2 text-[9px] leading-relaxed text-muted-foreground">
          Generated by Atlas from this load's details. Operational guidance only — pricing is entirely your call.
        </p>
      </div>
    </div>
  );
}
